import { Component } from "@theme/component";
import {
  GorgiasChatProgrammaticOpenEvent,
  KlaviyoFormProgrammaticOpenEvent,
} from "@theme/events";

/**
 * A custom element that dispatches programmatic open events for
 * third-party integrations (e.g., Gorgias Chat, Klaviyo Forms).
 *
 * Reads configuration values from its dataset (e.g., `actionType`,
 * `page`, `prefillMessage`, `formId`) and emits the appropriate
 * event when the element is interacted with.
 *
 * @extends {Component<Refs>}
 */
export class CustomEventComponent extends Component {
  /**
   * Defines which action type this element should trigger.
   * Values may include `"gorgias"` or `"klaviyo"`.
   *
   * @type {string|undefined}
   * @private
   */
  #actionType = undefined;

  /**
   * Lifecycle hook called when the element is inserted into the DOM.
   *
   * Initializes the action type from the element's `data-action-type` attribute.
   */
  connectedCallback() {
    super.connectedCallback();

    this.#actionType = this.dataset.actionType;
  }

  /**
   * Lifecycle hook called when the element is removed from the DOM.
   *
   * Currently defers to the parent implementation for cleanup.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Handles click interactions on the element.
   *
   * - If the action type is `"gorgias"`, dispatches a
   *   {@link GorgiasChatProgrammaticOpenEvent} with optional
   *   `page` and `prefillMessage` data attributes.
   *
   * - If the action type is `"klaviyo"`, dispatches a
   *   {@link KlaviyoFormProgrammaticOpenEvent} using the
   *   `formId` data attribute.
   *
   * @fires GorgiasChatProgrammaticOpenEvent
   * @fires KlaviyoFormProgrammaticOpenEvent
   */
  handleClick = () => {
    if (this.#actionType == "gorgias") {
      this.dispatchEvent(
        new GorgiasChatProgrammaticOpenEvent(
          this.dataset.page,
          this.dataset.prefillMessage,
        ),
      );
    }

    if (this.#actionType == "klaviyo" && this.dataset.formId) {
      this.dispatchEvent(
        new KlaviyoFormProgrammaticOpenEvent(this.dataset.formId),
      );
    }
  };
}

if (!customElements.get("custom-event-component")) {
  customElements.define("custom-event-component", CustomEventComponent);
}
